import React from 'react';
import type { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';

import Grid from 'components/Grid/Grid';
import { CardSkeleton } from 'components/Skeleton';
import GridRowCountContext from 'contexts/GridRowCountContext';
import HideUnlessMouseOver from 'components/HideUnlessMouseOver';
import IncompatibleComparison from 'components/IncompatibleComparison';
import MetricMatrixContext from 'contexts/MetricMatrixContext';

const MatrixView = ({
  isCompatibleComparison,
  gridOptions,
  columnDefs,
  totals,
  gridId,
  onDrillDown,
  onGridReady,
  rowCount,
  noDataToDisplay,
  isInitialLoading,
  autoSizeFirstColumnOnly,
  additionalAutoSizeColumnIds,
}: {
  isCompatibleComparison: boolean | undefined;
  gridOptions: GridOptions | undefined;
  columnDefs: ColDef[];
  totals: any;
  gridId: string;
  onDrillDown: (term: string, ctrlKey?: boolean) => void;
  onGridReady: (event: GridReadyEvent) => void;
  rowCount: number;
  noDataToDisplay: boolean;
  isInitialLoading: boolean;
  autoSizeFirstColumnOnly: boolean;
  additionalAutoSizeColumnIds?: string[];
}) => {
  if (!gridOptions) {
    return null;
  }

  if (!isCompatibleComparison) {
    return <IncompatibleComparison />;
  }

  // Initial load shows the same calm card skeleton as every other gadget,
  // rather than ag-Grid's spinner overlay, so a loading canvas reads as one
  // continuous skeleton.
  if (isInitialLoading) {
    return <CardSkeleton />;
  }

  return (
    <MetricMatrixContext.Provider value={{ onDrillDown }}>
      <GridRowCountContext.Provider value={{ rowCount }}>
        <HideUnlessMouseOver>
          <Grid
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            gridOptions={gridOptions}
            totalsRow={totals}
            gridId={gridId}
            noDataToDisplay={noDataToDisplay}
            // Initial load is handled by the CardSkeleton early-return above,
            // so the grid never needs to show its own loading overlay.
            isLoading={false}
            autoSizeFirstColumnOnly={autoSizeFirstColumnOnly}
            additionalAutoSizeColumnIds={additionalAutoSizeColumnIds}
            layoutOnFirstRender={true}
            layoutOnColumnChange={true}
            layoutOnModelUpdated={true}
            hasBorders={false}
            hasDarkHeader={false}
            enableHeadlessRefit
          />
        </HideUnlessMouseOver>
      </GridRowCountContext.Provider>
    </MetricMatrixContext.Provider>
  );
};

export default MatrixView;
